/**
 * Direction badges per challenge card (Community Cookbook tier-pill style).
 */

import { challengeBadgeClassName } from '../components/RecipeBook/recipeBookStyles';

function profileFirstName(name) {
	if (!name?.trim()) return null;
	return name.trim().split(/\s+/)[0];
}

function possessiveFirstName(name) {
	const first = profileFirstName(name);
	if (!first) return 'Their';
	return /s$/i.test(first) ? `${first}'` : `${first}'s`;
}

function displayName(name) {
	return profileFirstName(name) || name || 'Profile';
}

function fromBadge(name, variant = 'from') {
	if (!name) return null;
	return { text: `From ${displayName(name)}`, variant };
}

function statusBadge(text, variant = 'muted') {
	return text ? { text, variant } : null;
}

function compactBadges(list) {
	return list.filter(Boolean);
}

export function getChallengePerspective(challenge) {
	if (!challenge) return null;

	const isSelfChallenge =
		challenge.kind === 'friend' &&
		challenge.challenger?.id != null &&
		challenge.challenger.id === challenge.challenged?.id;
	const needsYou = Boolean(challenge.can_accept || challenge.can_decline || challenge.can_complete || challenge.can_fail);
	const waitingOnAccept =
		challenge.status === 'pending_acceptance' && challenge.can_cancel && !needsYou;
	const activeWaiting =
		(challenge.status === 'active' || (challenge.status === 'expired' && (challenge.can_complete || challenge.can_fail))) &&
		!needsYou &&
		challenge.role === 'challenger';

	if (challenge.kind === 'friend') {
		if (isSelfChallenge && challenge.mutual_support?.variant === 'companion_link' && needsYou) {
			const badges = compactBadges([
				statusBadge('Also doing', 'household'),
				statusBadge('Your turn', 'forYou')
			]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: true,
				waiting: false
			};
		}

		if (isSelfChallenge && needsYou) {
			const badges = compactBadges([statusBadge('Your challenge', 'forYou'), statusBadge('Your turn', 'forYou')]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: true,
				waiting: false
			};
		}

		const sent = challenge.role === 'challenger';
		const other = sent ? challenge.challenged : challenge.challenger;
		const otherName = displayName(other?.first_name || other?.full_name || 'friend');

		if (sent) {
			const waiting = waitingOnAccept || activeWaiting;
			const badges = compactBadges([
				waiting ? statusBadge('Waiting', 'muted') : statusBadge('Sent', 'sent')
			]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: false,
				waiting
			};
		}

		if (needsYou) {
			const badges = compactBadges([fromBadge(otherName, 'forYou'), statusBadge('Your turn', 'forYou')]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: true,
				waiting: false
			};
		}

		const badges = compactBadges([fromBadge(otherName, 'muted')]);
		return {
			badges,
			badgeLabel: badges.map((b) => b.text).join(' · '),
			urgent: false,
			waiting: false
		};
	}

	const from = challenge.challenger_profile?.name;
	const to = challenge.challenged_profile?.name;
	const toFirst = profileFirstName(to) || to;
	const isHouseholdSelfChallenge =
		from && to && from.trim().toLowerCase() === to.trim().toLowerCase();

	if (isHouseholdSelfChallenge && needsYou) {
		const badges = compactBadges([statusBadge('Your challenge', 'forYou'), statusBadge('Your turn', 'forYou')]);
		return {
			badges,
			badgeLabel: badges.map((b) => b.text).join(' · '),
			urgent: true,
			waiting: false
		};
	}

	if (challenge.status === 'pending_acceptance') {
		if (needsYou && toFirst) {
			const badges = compactBadges([
				fromBadge(from, 'from'),
				statusBadge(`${possessiveFirstName(to)} turn`, 'forYou')
			]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: true,
				waiting: false
			};
		}

		const badges = compactBadges([
			fromBadge(from, 'from'),
			toFirst ? statusBadge(`Waiting on ${toFirst}`, 'muted') : statusBadge('Pending', 'muted')
		]);
		return {
			badges,
			badgeLabel: badges.map((b) => b.text).join(' · '),
			urgent: false,
			waiting: true
		};
	}

	if (challenge.status === 'active' || (challenge.status === 'expired' && (challenge.can_complete || challenge.can_fail))) {
		const sent = challenge.role === 'challenger' || activeWaiting;
		if (sent) {
			const badges = compactBadges([
				fromBadge(from, 'from'),
				toFirst ? statusBadge(`Waiting on ${toFirst}`, 'muted') : statusBadge('Waiting', 'muted')
			]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: false,
				waiting: true
			};
		}
		if (needsYou && toFirst) {
			const badges = compactBadges([
				fromBadge(from, 'from'),
				statusBadge(`${possessiveFirstName(to)} doing`, 'forYou')
			]);
			return {
				badges,
				badgeLabel: badges.map((b) => b.text).join(' · '),
				urgent: true,
				waiting: false
			};
		}
		const badges = compactBadges([fromBadge(from, 'from'), statusBadge('Active', 'household')]);
		return {
			badges,
			badgeLabel: badges.map((b) => b.text).join(' · '),
			urgent: false,
			waiting: false
		};
	}

	const badges = compactBadges([fromBadge(from, 'from')]);
	return {
		badges,
		badgeLabel: badges.map((b) => b.text).join(' · '),
		urgent: false,
		waiting: false
	};
}

export function sortChallengesByRecency(challenges) {
	return [...challenges].sort((a, b) => {
		const aMs = a?.created_at ? new Date(a.created_at).getTime() : NaN;
		const bMs = b?.created_at ? new Date(b.created_at).getTime() : NaN;
		const aTime = Number.isFinite(aMs) ? aMs : 0;
		const bTime = Number.isFinite(bMs) ? bMs : 0;
		return bTime - aTime;
	});
}

export function groupChallengesForAllView(challenges) {
	const needsYou = [];
	const waiting = [];
	const rest = [];

	sortChallengesByRecency(challenges).forEach((c) => {
		const p = getChallengePerspective(c);
		if (p?.urgent) needsYou.push(c);
		else if (p?.waiting) waiting.push(c);
		else rest.push(c);
	});

	return [
		{ id: 'needs-you', title: 'Needs your attention', items: needsYou },
		{ id: 'waiting', title: 'Waiting on them', items: waiting },
		{ id: 'other', title: 'Other challenges', items: rest, hideWhenEmpty: true }
	].filter((section) => !section.hideWhenEmpty || section.items.length > 0);
}

export { challengeBadgeClassName };
